import { AxiosErrorResponse } from '@/@types/Errors';
import { usePermissions } from '@/auth/PermissionsContext';
import { useToast } from '@/hooks/use-toast';
import { isChallengeTokenError } from '@/hooks/useSecurityChallenge';
import { z } from '@/translations';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { WalletService } from '@/services/wallet.service';
import useModalStore from '@/store/useModalStore';

type WithdrawalFormData = {
  amount_requested: string;
  type?: string;
};

export const useWithdrawal = (
  currency: string,
  setServerError?: (msg: string) => void,
  type?: string,
  availableBalanceInCents?: number,
  payoutCurrency?: string,
  onChallengeTokenError?: () => void,
) => {
  const { t } = useTranslation(['zod', 'wallet']);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isPrimary } = usePermissions();

  const displayCurrency = typeof currency === 'string' ? currency : 'BRL';
  const currencyPrefix = displayCurrency === 'USD' ? 'US$' : 'R$';
  const MIN_AMOUNT_CENTS = displayCurrency === 'USD' ? 5000 : 10000;
  const minFormatted = displayCurrency === 'USD' ? 'US$ 50,00' : 'R$ 100,00';

  const WithdrawalSchema = z.object({
    amount_requested: z
      .string()
      .min(1, { message: t('required_field') })
      .refine(
        (value) => {
          const amount = parseFloat(
            value.replace(/[^0-9,-]/g, '').replace(',', '.'),
          );
          return amount > 0;
        },
        { message: t('wallet:withdrawal_validation.greater_than_zero') },
      )
      .refine(
        (value) => {
          const amount = parseFloat(
            value.replace(/[^0-9,-]/g, '').replace(',', '.'),
          );
          return amount >= MIN_AMOUNT_CENTS;
        },
        { message: t('wallet:withdrawal_validation.min_amount', { amount: minFormatted }) },
      )
      .refine(
        (value) => {
          if (availableBalanceInCents === undefined) return true;
          const amount = parseFloat(
            value.replace(/[^0-9,-]/g, '').replace(',', '.'),
          );
          return amount <= availableBalanceInCents;
        },
        { message: t('wallet:withdrawal_validation.exceeds_balance') },
      )
      .refine(
        () => {
          if (availableBalanceInCents === undefined) return true;
          return availableBalanceInCents > 0;
        },
        { message: t('wallet:withdrawal_validation.no_balance') },
      ),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<WithdrawalFormData>({
    resolver: zodResolver(WithdrawalSchema),
    defaultValues: {
      amount_requested: '0,00',
    },
  });

  const { closeModal, openModal } = useModalStore();

  const {
    mutate,
    isPending: isWithdrawing,
    isError,
    isSuccess,
  } = useMutation({
    mutationFn: async ({
      data,
      challengeToken,
    }: {
      data: WithdrawalFormData;
      challengeToken?: string;
    }) => {
      const amount = parseFloat(
        data.amount_requested.replace(/[^0-9,-]/g, '').replace(',', '.'),
      );
      const shouldConvert =
        payoutCurrency &&
        payoutCurrency.toUpperCase() !== currency.toUpperCase();

      return WalletService.createWithdrawal(amount, currency, {
        type: type || undefined,
        payout_currency: shouldConvert ? payoutCurrency : undefined,
        challenge_token: challengeToken,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['wallet-withdrawal'],
        refetchType: 'all',
      });
      await queryClient.invalidateQueries({
        queryKey: ['wallet-data'],
        refetchType: 'all',
      });

      if (type === 'partner') {
        await queryClient.invalidateQueries({
          queryKey: ['partner-wallet'],
          refetchType: 'all',
        });
        await queryClient.invalidateQueries({
          queryKey: ['partner-commissions'],
          refetchType: 'all',
        });
      } else if (type === 'referral') {
        await queryClient.invalidateQueries({
          queryKey: ['refer-and-earn-settings'],
          refetchType: 'all',
        });
        await queryClient.invalidateQueries({
          queryKey: ['refer-and-earn-referrals'],
          refetchType: 'all',
        });
      }

      toast({
        description: t('wallet:withdrawal_success'),
        variant: 'success',
      });

      if (type === 'partner') {
        closeModal('withdrawal-form-partner');
      } else if (type === 'referral') {
        closeModal('withdrawal-form-referral');
      } else {
        closeModal('withdrawal-form');
      }
      openModal('withdrawal-registered');
    },
    onError: (error: AxiosError<AxiosErrorResponse>) => {
      if (isChallengeTokenError(error)) {
        onChallengeTokenError?.();
        return;
      }

      if (error.response?.status === 422) {
        const firstError = Object.values(error.response.data.errors)[0]?.[0];
        setServerError?.(firstError || t('wallet:generic_error'));
      } else if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.message || error.message;
        if (errorMessage === 'withdrawal_awaiting_approval_exists') {
          setServerError?.(t('wallet:withdrawal_awaiting_approval_exists'));
        } else if (
          errorMessage === 'You need to add an active USD bank account' ||
          errorMessage === 'active_usd_bank_account_required'
        ) {
          setServerError?.(
            [
              t('wallet:withdrawal_convert.active_usd_bank_account_required'),
              '',
              t('wallet:withdrawal_convert.active_usd_bank_account_required_how_to'),
              '',
              t('wallet:withdrawal_convert.active_usd_bank_account_required_steps'),
            ].join('\n'),
          );
        } else {
          setServerError?.(errorMessage);
        }
      } else {
        setServerError?.(error.message);
      }
    },
  });

  const onSubmit = (data: WithdrawalFormData, challengeToken?: string) => {
    if (!isPrimary) {
      toast({
        variant: 'destructive',
        description: t('wallet:withdrawal_validation.owner_only', {
          defaultValue: 'Apenas o proprietário da conta pode solicitar saques.',
        }),
      });
      return;
    }

    mutate({ data, challengeToken });
  };

  return {
    onSubmit,
    handleSubmit,
    register,
    control,
    errors,
    isLoading: isWithdrawing || isSubmitting,
    isError,
    isSuccess,
  };
};
